// src/components/profiles/Member/MemberProfile.tsx

"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";

import PostCarousel from "./components/posts/PostCarousel";
import ProfileCarousel from "./components/profile/ProfileCarousel";
import TestimoniesCarousel from "./components/testimonies/TestimoniesCarousel";

import MyProfileActions from "../../shared/profile-actions/MyProfileActions";
import FriendsDrawer from "../../shared/profile-actions/FriendsDrawer";

import { MemberProfileType } from "../../../types/profiles/memberProfileTypes";
import { PageSkeleton } from "../../common";

import MemberProfileLimitedOwner from "./MemberProfileLimitedOwner";
import { isOwnerProfileTemporarilyUnavailable } from "../../../utils/profiles/profileGate";

interface MemberProfileProps {
  member: MemberProfileType;
}

const MemberProfile: React.FC<MemberProfileProps> = ({ member }) => {
  const router = useRouter();

  const [friendsOpen, setFriendsOpen] = useState(false);

  const customUser = member?.user;

  if (!customUser) {
    return (
      <PageSkeleton
        variant="profile"
        showTextLines={8}
      />
    );
  }

  const isSafetyLimitedOwner =
    isOwnerProfileTemporarilyUnavailable(member);

  if (isSafetyLimitedOwner) {
    return (
      <MemberProfileLimitedOwner
        member={member}
      />
    );
  }

  const friends = member?.friends ?? [];

  return (
    <>
      <TestimoniesCarousel />

      <ProfileCarousel
        profileImageUrl={customUser.avatar_cdn_url ?? ""}
        placeholderSrc={customUser.avatar_url ?? customUser.profile_image ?? ""}
        name={customUser.name ?? ""}
        labelColor={customUser.label?.color ?? "defaultColor"}
        isVerifiedIdentity={customUser.is_verified_identity ?? false}
        isTownlitVerified={customUser.is_townlit_verified ?? false}
        customUser={customUser}
        spiritualRebirthDay={member.spiritual_rebirth_day ?? null}
        biography={member.biography ?? ""}
        vision={member.vision ?? ""}
        showFellowship={member.show_fellowship_in_profile ?? false}
        litcovenant={member.litcovenant ?? []}
        ownerUserId={customUser.id}
        serviceTypes={member.service_types ?? []}
        organizationMemberships={member.organization_memberships ?? []}
        academicRecord={member.academic_record ?? null}
        spiritualGifts={member.spiritual_gifts ?? null}
        showGiftsInProfile={member.show_gifts_in_profile ?? false}
        denominationBranchLabel={member.denomination_branch_label ?? undefined}
        denominationFamilyLabel={member.denomination_family_label ?? null}
        friends={friends}
      />

      <div className="tl-indicator-aurora-light">
        <MyProfileActions
          onEditProfile={() => router.push("/settings/information")}
          onOpenFriends={() => setFriendsOpen(true)}
          onOpenRequests={() => router.push("/settings/friendships?tab=requests")}
        />
      </div>

      <FriendsDrawer
        open={friendsOpen}
        onClose={() => setFriendsOpen(false)}
        friends={friends}
      />

      <PostCarousel />
    </>
  );
};

export default MemberProfile;